import {
  BufferGeometry,
  FileLoader,
  Group,
  Line,
  Loader,
  LoadingManager,
  Vector3,
} from 'three'
import { defaultLineMaterial } from '../../../../constants/three'

class GCodeLoader extends Loader {
  /**
   * Whether to split layers or not.
   *
   * @type {boolean}
   * @default false
   */
  splitLayer: boolean
  constructor(manager?: LoadingManager) {
    super(manager)

    this.splitLayer = false
  }

  load(
    url: string,
    onLoad: (group: Group) => void,
    onProgress?: (event: ProgressEvent) => void,
    onError?: (err: unknown) => void,
  ) {
    const loader = new FileLoader(this.manager)
    loader.setPath(this.path)
    loader.setRequestHeader(this.requestHeader)
    loader.setWithCredentials(this.withCredentials)
    loader.load(url, (text) => {
      try {
        onLoad(this.parse(text as string))
      }
      catch (e) {
        if (onError) {
          onError(e)
        }
        else {
          console.error(e)
        }

        this.manager.itemError(url)
      }
    }, onProgress, onError)
  }

  parse(data: string) {
    let lastPoint: Vector3
    let lastLine: Vector3[] = []
    const allLines: Vector3[][] = []

    const lines = data.replaceAll(/;.+/g, '').split('\n')

    for (let i = 0; i < lines.length; i++) {
      const tokens = lines[i].split(' ')
      const isNumberedLine = tokens.length > 1 && tokens[0].startsWith('N')
      const cmd = isNumberedLine
        ? tokens[1].toUpperCase()
        : tokens[0].toUpperCase()

      const args: Record<string, string | number | boolean | undefined> = {}
      tokens.splice(isNumberedLine ? 2 : 1).forEach(function (token) {
        if (token[0] !== undefined) {
          const key = token[0].toLowerCase()
          const value = Number.parseFloat(token.substring(1))
          args[key] = value
        }
      })

      // Process commands
      // G0/G1 – Linear Movement
      if (cmd === 'G0' || cmd === 'G1') {
        lastPoint = new Vector3((args.x as number), (args.y as number), (args.z as number))

        if (cmd === 'G1') {
          lastLine.push(lastPoint)
        }
        else {
          allLines.push(lastLine)
          lastLine = [lastPoint]
        }
      }
      else {
        // console.warn('THREE.GCodeLoader: Command not implemented:' + cmd)
      }
    }

    allLines.push(lastLine)

    const object = new Group()
    const material = defaultLineMaterial.clone()

    allLines.forEach((currentLine) => {
      const geometry = new BufferGeometry().setFromPoints(currentLine)
      const line = new Line(
        geometry,
        material,
      )

      object.add(line)
    })

    return object
  }
}

export default GCodeLoader
